/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';
import { Dialog, DialogActions } from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MikeDialogTitle from '../../DialogComponents/MikeDialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import mikeSharedTheme from '../../../styles/mikeSharedTheme';
import { IGetProject, IGetProjectPath, IGetCustomProject } from '../../../Models/IGetProject';
import { IGetDataset } from '../../../Models/IGetDataset';
import MikeProjectExplorer from '../MikeProjectExplorer';
import { IColumn } from '../../TableComponents/support';
import { useIntl } from 'react-intl';
import MikeButton from '../../mike-button';

const dialogActionsStyle = css`
  padding-right: ${mikeSharedTheme.spacing(3)};
  background-color: ${mikeSharedTheme.palette.background.paper};
  color: ${mikeSharedTheme.palette.background.paper};
  width: 100%;
  z-index: 11;
  box-shadow: 0px -4px 4px rgba(0, 0, 0, 0.16);
`;

const dialogContentStyle = css`
  padding-top: 0;
  background-color: ${mikeSharedTheme.palette.background.default};
`;

const dialogPaperStyle = css`
  width: 75vw;
  height: 75vh;
  min-height: 75vh;
  max-width: 75vw;
`;

const titleStyle = css`
  padding: ${mikeSharedTheme.spacing(1)};
  z-index: 11;
  border-radius: 4px 4px 0px 0px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.16);
  height: ${mikeSharedTheme.spacing(8)};
  color: ${mikeSharedTheme.palette.background.paper};
  width: 100%;
`;

interface IProps {
  cancelButtonLabel?: string; 
  columns?: Array<IColumn>;
  customFirstTableCellRender?: (
    item: IGetProject | IGetDataset | IGetCustomProject,
    isSelectable: boolean,
    isSelected: boolean,
    onClick: (
      event: React.MouseEvent<HTMLTableHeaderCellElement, MouseEvent>,
      item: IGetProject | IGetDataset | IGetCustomProject
    ) => void
  ) => React.ReactNode; 
  dialogTitle?: string;
  loading: boolean;
  noContentLabel?: string;
  okButtonDisabled?: boolean;
  okButtonLabel?: string;
  onChangePage?: (page: number) => void;
  onChangeRowsPerPage?: (rowsPerPage: number) => void;
  onHandleRequestSort: (
    orderBy: string | ((item: IGetProject | IGetDataset | IGetCustomProject) => string | number),
    order: 'asc' | 'desc'
  ) => void;
  onItemClick: (item: IGetProject | IGetDataset | IGetProjectPath | IGetCustomProject) => void;
  onSelectionChange: (selectedItems: Array<IGetProject | IGetDataset | IGetCustomProject>) => void;
  open: boolean;
  onCancel: () => void;
  onOk: () => void;
  order: 'asc' | 'desc';
  orderBy: string | ((item: IGetProject | IGetDataset | IGetCustomProject) => string | number);
  page?: number;
  pagination?: boolean;
  project?: IGetProject | IGetCustomProject;
  projectContent: Array<IGetProject | IGetDataset | IGetCustomProject>;
  projectPath: Array<IGetProjectPath>;
  rowsPerPage?: number;
  selectable: boolean;
  selectedItems: Array<IGetProject | IGetDataset | IGetCustomProject>;
  totalCount?: number;
}

const MikeProjectExplorerDialog = (props: IProps) => { 
  const intl = useIntl(); 

  const {    
    columns,
    customFirstTableCellRender,  
    dialogTitle,
    loading,
    noContentLabel = 'No content yet',
    okButtonLabel,
    okButtonDisabled,
    onChangePage,
    onChangeRowsPerPage,
    onHandleRequestSort,
    onItemClick,
    onSelectionChange,
    open,
    onCancel,
    onOk,
    order,
    orderBy,
    page,
    pagination,
    project,
    projectContent,
    projectPath,
    rowsPerPage,
    selectable,
    selectedItems,
    totalCount,
  } = props;

  return (
    <Dialog maxWidth={'lg'} onClose={onCancel} open={open} sx={{ "& .MuiPaper-root": dialogPaperStyle }}>
      <MuiDialogTitle css={titleStyle}>
        <MikeDialogTitle title={dialogTitle} onClose={onCancel} />
      </MuiDialogTitle>
      <MuiDialogContent css={dialogContentStyle}>
        <MikeProjectExplorer
          columns={columns}
          customFirstTableCellRender={customFirstTableCellRender}
          loading={loading}
          noContentLabel={noContentLabel}
          order={order}
          orderBy={orderBy}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onSelectionChange={onSelectionChange}
          onHandleRequestSort={onHandleRequestSort}
          onItemClick={onItemClick}
          page={page}
          pagination={pagination}
          project={project}
          projectPath={projectPath}
          selectedItems={selectedItems}
          projectContent={projectContent}
          rowsPerPage={rowsPerPage}
          selectable={selectable}
          totalCount={totalCount}
        />
      </MuiDialogContent>
      <DialogActions css={dialogActionsStyle}>
        <MikeButton onClick={onCancel} variant="text">
          {intl.formatMessage({id: 'projectExplorer.cancelButtonLabel'})}
        </MikeButton>
        <MikeButton variant="contained" onClick={onOk} color="secondary" disabled={okButtonDisabled}>
          {okButtonLabel}
        </MikeButton>
      </DialogActions>
    </Dialog> 
  );
};

export default MikeProjectExplorerDialog;
